import { FlightSegment } from 'constants/flight'

export type FareFamilyCostKey = 'adult' | 'all'

export function getFareFamilyCost(fareFamily: App.FlightFareFamily | undefined, costKey: FareFamilyCostKey): number {
  return fareFamily?.cost[costKey] ?? 0
}

export function getFareFamilyCostWithInsurance(fareFamily: App.FlightFareFamily | undefined, costKey: FareFamilyCostKey): number {
  return fareFamily?.costWithInsurance?.[costKey] ?? 0
}

export function getFareFamilyLuxPlusCostWithInsurance(fareFamily: App.FlightFareFamily | undefined, costKey: FareFamilyCostKey): number {
  return fareFamily?.luxPlusCostWithInsurance?.[costKey] ?? fareFamily?.costWithInsurance?.[costKey] ?? 0
}

export function getFareFamilyRegularCost(fareFamily: App.FlightFareFamily | undefined, costKey: FareFamilyCostKey): number {
  return fareFamily?.regularCost?.[costKey] ?? fareFamily?.cost[costKey] ?? 0
}

/**
 * The real, already-committed cost of a leg that's already been selected (e.g. the
 * departing leg while browsing the returning leg of a two-one-ways bundle) - the
 * fare family's own cost when one was chosen, insurance-aware, otherwise the plain
 * journey's fare. Mirrors the departing-side addition `getOverrideCost` already does
 * for the fare-family upsell list, so the outer tile and its modal agree.
 */
export function getCommittedLegPrice(fareFamily: App.FlightFareFamily | undefined, journey: App.JourneyV2 | undefined): number {
  if (fareFamily) {
    return fareFamily.withEmbeddedInsurance
      ? getFareFamilyCostWithInsurance(fareFamily, 'all')
      : getFareFamilyCost(fareFamily, 'all')
  }

  return journey?.price.all.totalFare ?? 0
}

export function getFareFamilyRegularCostWithInsurance(fareFamily: App.FlightFareFamily | undefined, costKey: FareFamilyCostKey): number {
  const fareCost = getFareFamilyCost(fareFamily, costKey)
  const protectedFareCost = fareFamily?.costWithInsurance?.[costKey] ?? fareCost
  let protectionPremium = 0
  if (fareFamily?.withEmbeddedInsurance) {
    protectionPremium = protectedFareCost - fareCost
  }
  return getFareFamilyRegularCost(fareFamily, costKey) + protectionPremium
}

function getCheapestPairedReturnFareFamily(
  pairedReturnFareFamilies: Array<App.FlightFareFamily>,
  costKey: FareFamilyCostKey,
): App.FlightFareFamily | undefined {
  return pairedReturnFareFamilies.reduce((cheapest, pairedFareFamily) => {
    if (!cheapest) return pairedFareFamily
    if (pairedFareFamily.cost[costKey] < cheapest.cost[costKey]) return pairedFareFamily
    return cheapest
  }, pairedReturnFareFamilies[0])
}

interface OverrideCostParams {
  fareFamily: App.FlightFareFamily
  costKey: FareFamilyCostKey
  segment: FlightSegment
  selectedDepartingFareFamily?: App.FlightFareFamily
  /**
   * Fare families for the returning leg that are paired with the departing
   * `fareFamily` being priced. Only used when `segment` is DEPARTING, to
   * estimate a full round-trip total using the cheapest paired return fare.
   */
  pairedReturnFareFamilies: Array<App.FlightFareFamily>
}

/**
 * Computes an additive round-trip total for a fare family option: departing
 * fare + returning fare, so upsell tiers always show a full total price
 * rather than each leg's independently-priced figure.
 */
export function getOverrideCost({
  fareFamily,
  costKey,
  segment,
  selectedDepartingFareFamily,
  pairedReturnFareFamilies,
}: OverrideCostParams): number {
  if (segment === FlightSegment.DEPARTING) {
    const cheapestPairedReturnFareFamily = getCheapestPairedReturnFareFamily(pairedReturnFareFamilies, costKey)
    const priceOfCheapestPairedReturnFareFamily = cheapestPairedReturnFareFamily?.cost[costKey] ?? 0

    if (fareFamily.withEmbeddedInsurance) {
      return getFareFamilyCostWithInsurance(fareFamily, costKey) + priceOfCheapestPairedReturnFareFamily
    }

    return getFareFamilyCost(fareFamily, costKey) + priceOfCheapestPairedReturnFareFamily
  }

  if (fareFamily.withEmbeddedInsurance) {
    if (selectedDepartingFareFamily?.withEmbeddedInsurance) {
      return getFareFamilyCostWithInsurance(selectedDepartingFareFamily, costKey) + getFareFamilyCostWithInsurance(fareFamily, costKey)
    }

    return getFareFamilyCost(selectedDepartingFareFamily, costKey) + getFareFamilyCostWithInsurance(fareFamily, costKey)
  }

  if (selectedDepartingFareFamily?.withEmbeddedInsurance) {
    return getFareFamilyCostWithInsurance(selectedDepartingFareFamily, costKey) + getFareFamilyCost(fareFamily, costKey)
  }

  return getFareFamilyCost(selectedDepartingFareFamily, costKey) + getFareFamilyCost(fareFamily, costKey)
}

/**
 * LuxPlus member-price equivalent of {@link getOverrideCost}.
 */
export function getOverrideLuxPlusMemberCost({
  fareFamily,
  costKey,
  segment,
  selectedDepartingFareFamily,
  pairedReturnFareFamilies,
}: OverrideCostParams): number {
  if (segment === FlightSegment.DEPARTING) {
    const cheapestPairedReturnFareFamily = getCheapestPairedReturnFareFamily(pairedReturnFareFamilies, costKey)
    const priceOfCheapestPairedReturnFareFamily = cheapestPairedReturnFareFamily?.cost[costKey] ?? 0

    let departingLegCost = getFareFamilyCost(fareFamily, costKey)
    if (fareFamily.withEmbeddedInsurance) {
      departingLegCost = getFareFamilyLuxPlusCostWithInsurance(fareFamily, costKey)
    }

    return departingLegCost + priceOfCheapestPairedReturnFareFamily
  }

  let departingCost = getFareFamilyCost(selectedDepartingFareFamily, costKey)
  if (selectedDepartingFareFamily?.withEmbeddedInsurance) {
    departingCost = getFareFamilyLuxPlusCostWithInsurance(selectedDepartingFareFamily, costKey)
  }

  let returningCost = getFareFamilyCost(fareFamily, costKey)
  if (fareFamily.withEmbeddedInsurance) {
    returningCost = getFareFamilyLuxPlusCostWithInsurance(fareFamily, costKey)
  }

  return departingCost + returningCost
}

/**
 * LuxPlus non-member ("regular") price equivalent of {@link getOverrideCost}.
 */
export function getOverrideLuxPlusRegularCost({
  fareFamily,
  costKey,
  segment,
  selectedDepartingFareFamily,
  pairedReturnFareFamilies,
}: OverrideCostParams): number {
  if (segment === FlightSegment.DEPARTING) {
    const cheapestPairedReturnFareFamily = getCheapestPairedReturnFareFamily(pairedReturnFareFamilies, costKey)

    return getFareFamilyRegularCostWithInsurance(fareFamily, costKey) + getFareFamilyRegularCostWithInsurance(cheapestPairedReturnFareFamily, costKey)
  }

  return getFareFamilyRegularCostWithInsurance(selectedDepartingFareFamily, costKey) + getFareFamilyRegularCostWithInsurance(fareFamily, costKey)
}
